import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {
	Button,
	Card,
	CardHeader,
	CardContent,
  Box,
  colors,
  Typography,
  Chip,
  LinearProgress,
  Tooltip,
} from '@material-ui/core';
import { PageHeader, PageSearch, PageEmpty, PAGE_KIT, usePageStyles } from 'components/Page/PageKit';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import _ from 'lodash';
import { Tabs, Tab } from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import routes from '../../utilities/route_constants';
import {GameFeedQuery, GameFeedData, GameFeedVars, IGameFeed} from '@pvplevelup/core/dist/queries/game/gameFeed';
import CustomSnackBar, { VariantIcon }  from 'components/Snackbar/CustomSnackBar';
import LocalizedStrings from 'react-localization';
import { AocClasses, ICharacter } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { ILobby } from '@pvplevelup/core/dist/interfaces/ILobby';
import { GameModes, IGame } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import moment from 'moment';
import { ZoneInformation } from '@pvplevelup/core/dist/interfaces/maps/IMaps';
import { blackColor } from 'assets/jss/material-dashboard-react';
import { List, ListRowProps } from 'react-virtualized';
import { VirtualizedList } from 'components/Table/VirtualizedList';
import { getRatingComponent } from 'views/PvP/game/GameScore';
import { classImages } from 'utilities/ClassImages';
import { MapThumbnailIcons } from 'utilities/map_icons';
import { hhmmss } from '@pvplevelup/core/dist/utility/time';
import { ArrowDropDown, ArrowDropUp, Remove, History as HistoryIcon, Map as MapIcon, Schedule, People } from '@material-ui/icons';
import { Waypoint } from 'react-waypoint';
import { MapInformation } from '@pvplevelup/core/dist/interfaces/maps/IMapInformation';

const strings = new LocalizedStrings({
  en: {
    title: "Past Games",
    search: "Search ...",
    header: "Past Games",
    subHeader: "Search for Past Games",
    nameHeader: "Name",
  },
  kr:{
    title: "액티브 PvP 로비",
    startLobbyBtn: "로비 시작",
    returnToLobbyBtn: "로비로 돌아 가기",
    search: "검색 ...",
    header: "커스텀 게임",
    subHeader: "커스텀 게임 검색",
    nameHeader: "이름",
  },
  ru: {
    title: "Активные лобби PvP",
    startLobbyBtn: "Начать лобби",
    returnToLobbyBtn: "Вернуться в лобби",
    search: "Поиск ...",
    header: "Кастомные игры",
    subHeader: "Поиск пользовательских игр",
    nameHeader: "название",
  },
  fr: {
    title: "Lobbies PvP actifs",
    startLobbyBtn: "Démarrer le lobby",
    returnToLobbyBtn: "Retour au lobby",
    search: "Chercher ...",
    header: "Jeux personnalisés",
    subHeader: "Rechercher des jeux personnalisés",
    nameHeader: "Nom",
  },
  es: {
    title: "Lobbies activos de PvP",
    startLobbyBtn: "Iniciar lobby",
    returnToLobbyBtn: "Regresar al lobby",
    search: "Buscar ...",
    header: "Juegos personalizados",
    subHeader: "Buscar juegos personalizados",
    nameHeader: "Nombre",
  },
  de: {
    title: "Aktive PvP-Lobbys",
    startLobbyBtn: "Starten Sie die Lobby",
    returnToLobbyBtn: "Kehre zur Lobby zurück",
    search: "Suche ...",
    header: "Benutzerdefinierte Spiele",
    subHeader: "Suche nach benutzerdefinierten Spielen",
    nameHeader: "Name",
  }
})

const useStyles = makeStyles((theme) => {
	return {
		title: {
			paddingTop: '8px'
    },
		cardCategoryWhite: {
			'&,& a,& a:hover,& a:focus': {
				color: 'rgba(255,255,255,.62)',
				margin: '0',
				fontSize: '14px',
				marginTop: '0',
				marginBottom: '0'
			},
			'& a,& a:hover,& a:focus': {
				color: '#FFFFFF'
			}
		},
		cardTitleWhite: {
			color: '#FFFFFF',
			marginTop: '0px',
			minHeight: 'auto',
			fontWeight: 'bold',
			fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
			marginBottom: '3px',
			textDecoration: 'none',
			'& small': {
				color: '#777',
				fontSize: '65%',
				fontWeight: '400',
				lineHeight: '1'
			}
		},
		// the list sits in a panel; the rows are cards inside it
		listPanel: {
			position: 'relative',
			overflow: 'hidden',
			padding: theme.spacing(1.25, 1, 0.25, 1.25),
		},
    buttonProgress: {
      color: theme.palette.primary.main,
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -12,
      marginLeft: -12,
    }
	};
});

const TEAM_COLORS = ['rgb(62, 124, 255)', 'rgb(228, 54, 55)'];
const TEAM_TINTS = ['rgba(62, 124, 255, 0.1)', 'rgba(228, 54, 55, 0.1)'];
const TEAM_BORDERS = ['rgba(62, 124, 255, 0.35)', 'rgba(228, 54, 55, 0.35)'];
const LOSER_SCORE = 'rgba(255, 255, 255, 0.5)';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useRowStyles = makeStyles((theme) => ({
  // the space between cards lives in the measured row (padding), so CellMeasurer counts it
  wrap: {
    padding: theme.spacing(0, 0.5, 1, 0),
  },
  card: {
    display: 'grid',
    gridTemplateColumns: '124px minmax(170px, 1fr) 140px minmax(320px, 1.6fr)',
    alignItems: 'center',
    gridGap: theme.spacing(2),
    padding: theme.spacing(1, 1.5, 1, 1),
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.025)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s',
    '&:hover': {
      background: 'rgba(66, 165, 245, 0.06)',
      borderColor: 'rgba(66, 165, 245, 0.35)',
    },
  },
  map: {
    position: 'relative',
    height: 70,
    borderRadius: 9,
    overflow: 'hidden',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.08)',
    display: 'grid',
    placeItems: 'center',
    color: 'rgba(255, 255, 255, 0.3)',
  },
  /** the result colour down the left edge of the thumbnail */
  resultBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
  },
  mapName: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '10px 7px 3px 9px',
    fontSize: '0.68rem',
    fontWeight: 700,
    color: '#fff',
    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.85))',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  mode: {
    fontSize: '1rem',
    fontWeight: 800,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pills: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridGap: 6,
    margin: '5px 0 6px',
  },
  meta: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridGap: 12,
    alignItems: 'center',
    fontSize: '0.78rem',
    color: PAGE_KIT.muted,
    whiteSpace: 'nowrap',
    '& svg': {
      fontSize: 14,
      marginRight: 4,
      verticalAlign: -2,
      color: PAGE_KIT.faint,
    },
  },
  score: {
    textAlign: 'center',
    padding: theme.spacing(0.75, 0.5),
    borderRadius: 10,
    background: 'rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.04)',
  },
  scoreLine: {
    fontSize: '1.45rem',
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: '0.04em',
  },
  scoreMeta: {
    marginTop: 2,
    fontSize: '0.72rem',
    color: PAGE_KIT.muted,
    whiteSpace: 'nowrap',
  },
  teams: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gridGap: theme.spacing(1),
  },
  team: {
    minWidth: 0,
    padding: theme.spacing(0.5, 1, 0.75),
    borderRadius: 8,
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid transparent',
  },
  teamLabel: {
    marginBottom: 2,
    fontSize: '0.64rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  player: {
    display: 'grid',
    gridTemplateColumns: '18px minmax(0, 1fr)',
    gridGap: 6,
    alignItems: 'center',
    fontSize: '0.82rem',
    lineHeight: '21px',
    '& img': {
      width: 18,
      height: 18,
    },
    '& span': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
}));

interface Props {}

const RenderRow: React.FC<{props:ListRowProps,game:IGame,measure:()=>void,gameFeed:IGameFeed | null,fetchMoreRecords:()=>void,history:any,rankedFilter?:boolean | null}> = ({props,game,measure,gameFeed,fetchMoreRecords,history,rankedFilter}) => {
  let styles = useRowStyles();
  let page = usePageStyles();
  let zone = ZoneInformation[game.zone || ""] || null;
    let currentMap = zone ? zone.mapInfo[game.map || ""] : null;
    let image = game.zone && game.map ? MapThumbnailIcons[game.zone || ""][game.map || ""] : null;
    let participant = game?.performance;
    let winner = game.isTie ? null : game.winner;
    let mode = "";
    let {index} = props;
    switch(game.mode){
      case GameModes.TeamDeathMatch:
        mode = "tdm";
        break;
      case GameModes.KingoftheHill:
        mode = "koh";
        break;
      case GameModes.Conquest:
        mode = "conquest";
        break;
      case GameModes.CaptureTheFlag:
        mode = "capturetheflag";
        break;
    }
    useEffect(()=>{
      measure();
    },[])
    let team1MMR = game.teamMMR && game.teamMMR[0] != null && game.teamCMMR && game.teamCMMR[0] != null ? ((game.teamMMR[0] + game.teamCMMR[0]) / 2) : 0;
    let team2MMR = game.teamMMR && game.teamMMR[1] != null && game.teamCMMR && game.teamCMMR[1] != null ? ((game.teamMMR[1] + game.teamCMMR[1]) / 2) : 0;
    const resultColor = winner == null ? 'rgba(255,255,255,0.35)' : TEAM_COLORS[winner];
    const mmrDiff = Math.round(Math.abs(team1MMR - team2MMR));
    // proving grounds (open world) games are their own kind; otherwise ranked or unranked, falling back on the tab
    // for games cached before the feed carried these
    const ranked: boolean | null | undefined = game.ranked != null ? game.ranked : rankedFilter;
    const kind = game.openworld ? GAME_KINDS.provinggrounds : ranked == null ? null : ranked ? GAME_KINDS.ranked : GAME_KINDS.unranked;
    const when = moment(game.createdAt);
    const scoreColor = (num: number) => winner == null ? undefined : winner == num ? TEAM_COLORS[num] : LOSER_SCORE;
    const team = (num: number) => (
      <div className={styles.team} style={winner == num ? {background: TEAM_TINTS[num], borderColor: TEAM_BORDERS[num]} : undefined}>
        <div className={styles.teamLabel} style={{color: TEAM_COLORS[num]}}>Team {num + 1}{winner == num ? ' · Victory' : ''}</div>
        {game.players?.filter((p)=>p.team === num).map((player,i)=>(
          <div key={i} className={styles.player}>
            <img src={classImages.getColorIcon(player.class as AocClasses)} title={player.class} />
            <span style={{opacity: winner == null || winner == num ? 1 : 0.65}}>{player.name}</span>
          </div>
        ))}
      </div>
    );
    return (
      <div key={game._id} className={styles.wrap}>
        <div className={styles.card} onClick={()=>history.push(`/game/${mode}/${game._id}`)}>
          <div className={styles.map} style={{backgroundImage: image ? `url(${image})` : undefined}}>
            {!image && <MapIcon />}
            <span className={styles.resultBar} style={{background: resultColor}} />
            {game.map && MapInformation[game.map] && <div className={styles.mapName}>{MapInformation[game.map].name}</div>}
          </div>
          <div style={{minWidth: 0}}>
            <div className={styles.mode}>{game.mode}</div>
            <div className={styles.pills}>
              {kind && <span className={page.pill} style={{color: kind.color, background: kind.background}}>{kind.label}</span>}
              <span className={page.pill} style={{color: '#fff', background: winner == null ? 'rgba(255, 255, 255, 0.12)' : resultColor}}>{winner == null ? 'Tie' : `Team ${winner + 1} win`}</span>
            </div>
            <div className={styles.meta}>
              <Tooltip title={when.format('MMM Do YYYY, h:mm a')}>
                <span>{when.format("MMM D, YYYY h:mm a")}</span>
              </Tooltip>
              <span><Schedule />{hhmmss((game.totalTimeElapsed || 0),false)}</span>
              <span><People />{game.players?.length || 0}</span>
            </div>
          </div>
          <div className={styles.score}>
            <div className={styles.scoreLine}>
              <span style={{color: scoreColor(0)}}>{game.teams?.[0]?.score}</span>
              <span style={{opacity: 0.35}}> : </span>
              <span style={{color: scoreColor(1)}}>{game.teams?.[1]?.score}</span>
            </div>
            <div className={styles.scoreMeta}>{game.teams?.[0]?.kills ?? '-'} : {game.teams?.[1]?.kills ?? '-'} kills</div>
            <Tooltip title="Average MMR difference between the teams">
              <div className={styles.scoreMeta}>
                MMR diff <span style={{fontWeight: 800, color: team1MMR > team2MMR ? TEAM_COLORS[0] : team2MMR > team1MMR ? TEAM_COLORS[1] : '#fff'}}>{mmrDiff}</span>
              </div>
            </Tooltip>
          </div>
          <div className={styles.teams}>
            {team(0)}
            {team(1)}
          </div>
        </div>
        {(index == (gameFeed?.games.length || 0) - 4 && gameFeed?.hasNext) && <Waypoint onEnter={fetchMoreRecords} />}
      </div>
    );
}

const GAME_KINDS = {
  ranked: {label: 'Ranked', color: '#90caf9', background: 'rgba(66, 165, 245, 0.16)'},
  unranked: {label: 'Unranked', color: 'rgba(255, 255, 255, 0.85)', background: 'rgba(255, 255, 255, 0.08)'},
  provinggrounds: {label: 'PvP Proving Grounds', color: '#ce93d8', background: 'rgba(171, 71, 188, 0.18)'},
};

/** "All" is the default and shows ranked, unranked and open world games. */
enum GameFilter {
  All = "All",
  Ranked = "Ranked",
  Unranked = "Unranked",
}

const filterToRanked = (filter: GameFilter) => filter == GameFilter.All ? null : filter == GameFilter.Ranked;

const PvPList: React.FC<Props> = (props) => {
	const history = useHistory();
	const classes = useStyles();
  const page = usePageStyles();

  const navigateToGame = (id: string, mode: string) => {
    switch(mode){
      case GameModes.TeamDeathMatch:
        history.push(`/game/tdm/${id}`)
        break;
      case GameModes.KingoftheHill:
        history.push(`/game/koh/${id}`)
        break;
      case GameModes.Conquest:
        history.push(`/game/conquest/${id}`)
        break;
      case GameModes.CaptureTheFlag:
        history.push(`/game/capturetheflag/${id}`)
        break;
    }

  }

	const [ search, setSearch ] = useState('');
	const [ typing, setTyping ] = useState(false);
  const [ typingTimeout, setTypingTimeout ] = useState<NodeJS.Timeout | undefined>();
  const [showError, setShowError] = useState(true);
  const [filter, setFilter] = useState<GameFilter>(GameFilter.All);

  const [loadData, { subscribeToMore, ...result }] = useLazyQuery<GameFeedData, GameFeedVars>(GameFeedQuery);

	const onChangeSearch = (newSearch: string) => {
		if (typingTimeout) clearTimeout(typingTimeout);

		setSearch(newSearch);
		setTyping(true);
		setTypingTimeout(
			setTimeout(() => {
				loadData({ variables:{cursor: null, search: newSearch, ranked: filterToRanked(filter)} });
				setTyping(false);
			}, 600)
		);
	};

	const {
		data,
		loading,
		fetchMore
  } = result;

  const fetchMoreGameRecords = ()=>{
    if(!loading && data?.gameFeed?.hasNext && fetchMore){
      fetchMore({
        // note this is a different query than the one used in the
        // Query component
        query: GameFeedQuery,
        variables: { cursor: data.gameFeed.next, search, ranked: filterToRanked(filter) },
        updateQuery: (
          previousResult,
          { fetchMoreResult }: { fetchMoreResult?: GameFeedData | undefined }
        ) => {
          const previousGameFeed = previousResult.gameFeed;
          const newGames = fetchMoreResult
            ? fetchMoreResult.gameFeed.games
            : [];
          const newNext = fetchMoreResult ? fetchMoreResult.gameFeed.next : '';
          const newPrevious = fetchMoreResult ? fetchMoreResult.gameFeed.previous : '';
          const newHasNext = fetchMoreResult
            ? fetchMoreResult.gameFeed.hasNext
            : false;
          const newHasPrevious = fetchMoreResult
            ? fetchMoreResult.gameFeed.hasPrevious
            : false;
          const allGames = _.uniqBy(
            [ ...previousGameFeed.games, ...newGames ],
            (item) => item._id
          );

          return {
            gameFeed: {
              next: newNext,
              previous: newPrevious,
              hasNext: newHasNext,
              hasPrevious: newHasPrevious,
              games: allGames,
              __typename: previousGameFeed.__typename
            }
          };
        }
      });
    }
  }

  let [currentLocale, setCurrentLocale] = useState<string | undefined>("");
  const listRef = useRef<List>(null);

  useEffect(()=>{
    strings.setLanguage((data && data.locale) ? data.locale : 'en');
    setCurrentLocale(data?.locale);
  },[data?.locale])

  useEffect(()=>{
    loadData({variables:{cursor:null,search,ranked:filterToRanked(filter)}})
  },[filter])

  const games = data?.gameFeed?.games || [];

	return (
		<Grid container spacing={2} className="content-container">
			<Grid item xs={12}>
        <PageHeader
          title={strings.title}
          subtitle="Every recorded match, newest first. Open one for the full scoreboard."
          icon={<HistoryIcon />}
          actions={<PageSearch value={search} onChange={(value) => onChangeSearch(value)} placeholder="Search by game name ..." width={320} />}
        >
          <Tabs value={filter} onChange={(event, value) => setFilter(value)} indicatorColor="primary" textColor="primary">
            <Tab value={GameFilter.All} label="All" />
            <Tab value={GameFilter.Ranked} label="Ranked" />
            <Tab value={GameFilter.Unranked} label="Unranked" />
          </Tabs>
        </PageHeader>
      </Grid>
      <Grid item xs={12}>
        <div className={`${page.panel} ${classes.listPanel}`}>
          {(loading || typing) && <LinearProgress style={{position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 2}} />}
          <div style={{height:`calc(100vh - 300px)`, minHeight: 320}}>
            <VirtualizedList
              reference={listRef}
              data={games}
              overscanRowCount={10}
              minWidth={900}
              customRenderer={(props:ListRowProps,game:IGame,measure:()=>void)=>{
                return <RenderRow props={props} game={game} measure={measure} gameFeed={data?.gameFeed || null} fetchMoreRecords={fetchMoreGameRecords} history={history} rankedFilter={filterToRanked(filter)} />
              }}
            />
          </div>
          {(!loading && !typing && games.length == 0) &&
            <div style={{position: 'absolute', top: 24, left: 0, right: 0}}>
              <PageEmpty
                icon={<HistoryIcon />}
                title={search ? `No games match "${search}"` : `No ${filter == GameFilter.All ? '' : `${filter.toLowerCase()} `}games yet`}
                text={search ? 'Try a different game name, or clear the search.' : 'Games you and others play show up here once they finish.'}
              />
            </div>
          }
        </div>
			</Grid>
		</Grid>
	);
};
export default PvPList;
